import { useCallback, useState } from 'react';

export type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

interface ParsedResponse {
  code?: string;
  message?: string;
  meta?: {
    message?: string;
    detail?: string;
  };
}

export interface UseFormSubmitResult {
  status: FormStatus;
  message: string;
  submit: (form: HTMLFormElement, endpoint: string) => Promise<boolean>;
  reset: () => void;
}

export default function useFormSubmit(): UseFormSubmitResult {
  const [status, setStatus] = useState<FormStatus>('idle');
  const [message, setMessage] = useState('');

  const reset = useCallback(() => {
    setStatus('idle');
    setMessage('');
  }, []);

  const submit = useCallback(async (form: HTMLFormElement, endpoint: string) => {
    const formData = new FormData(form);
    const trap = String(formData.get('website_alt') ?? '').trim();

    // 兜底字段被填写：静默按成功处理，不发送任何数据
    if (trap) {
      setStatus('success');
      setMessage('提交成功，感谢你的反馈。');
      return true;
    }
    formData.delete('website_alt');

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === 'string') payload.append(key, value);
    });

    setStatus('submitting');
    setMessage('');

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: ParsedResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as ParsedResponse;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        '';
      const code = parsed?.code;

      if (!response.ok || code !== 'OK' || /spam|spammy/i.test(String(serverMsg))) {
        setStatus('error');
        setMessage(serverMsg || '提交失败，请稍后重试。');
        return false;
      }

      setStatus('success');
      setMessage(serverMsg || '提交成功，我们会尽快回复。');
      form.reset();
      return true;
    } catch {
      setStatus('error');
      setMessage('网络异常，提交失败，请稍后重试。');
      return false;
    }
  }, []);

  return { status, message, submit, reset };
}